import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Button, Container, IconButton, Link, Typography } from '@mui/material'
import React from 'react'

import LazyImage from '@/src/components/common/ui/LazyImage'

interface Destination {
    id: string
    name: string
    imageUrl: string
    href: string
    title?: string
}

type TranslationFunction = (key: string) => string

interface TrendingDestinationsProps {
    title: string
    destinations: Destination[]
    isRTL: boolean
    scrollContainerRef: React.RefObject<HTMLDivElement>
    onScroll: (direction: 'left' | 'right') => void
    t: TranslationFunction
    onDestinationClick?: (href: string) => boolean
}

export const TrendingDestinations: React.FC<TrendingDestinationsProps> = ({
    title,
    destinations,
    isRTL,
    scrollContainerRef,
    onScroll,
    t,
    onDestinationClick
}) => {
    if (!destinations || destinations.length === 0) {
        return null
    }

    return (
        <Box
            sx={{
                py: { xs: 3, md: 4 },
                // bgcolor: 'grey.50'
            }}
        >
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
                <MobileView title={title} destinations={destinations} t={t} onDestinationClick={onDestinationClick} />
                <DesktopView
                    title={title}
                    destinations={destinations}
                    isRTL={isRTL}
                    scrollContainerRef={scrollContainerRef}
                    onScroll={onScroll}
                    t={t}
                    onDestinationClick={onDestinationClick}
                />
            </Container>
        </Box>
    )
}

const MobileView: React.FC<{
    title: string
    destinations: Destination[]
    t: TranslationFunction
    onDestinationClick?: (href: string) => boolean
}> = ({ title, destinations, t, onDestinationClick }) => {
    return (
        <>
            {/* Header Section */}
            <Box
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    mb: 2,
                    position: 'relative'
                }}
            >
                <Typography
                    component="h2"
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '1.125rem',
                        textAlign: 'left'
                    }}
                >
                    {title}
                </Typography>
                {/* Mobile trending badge */}
                <Box
                    sx={theme => ({
                        position: 'absolute',
                        right: 0,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.6)',
                        borderRadius: '20px',
                        px: 1.5,
                        py: 0.5
                    })}
                >
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'common.white'
                        }}
                    >
                        {t('trending')}
                    </Typography>
                </Box>
            </Box>

            {/* Mobile Grid View */}
            <Box
                sx={{
                    display: { xs: 'grid', md: 'none' },
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2
                }}
            >
                {destinations.map((destination, index) => (
                    <Link
                        key={destination.id}
                        href={destination.href}
                        onClick={event => {
                            if (onDestinationClick?.(destination.href)) {
                                event.preventDefault()
                            }
                        }}
                        target="_blank"
                        sx={{
                            position: 'relative',
                            borderRadius: 2,
                            overflow: 'hidden',
                            height: '160px',
                            bgcolor: 'common.white',
                            display: 'block',
                            textDecoration: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        <LazyImage
                            src={destination.imageUrl}
                            alt={destination.name}
                            sizes="(max-width: 768px) 50vw, 200px"
                            priority={index < 2}
                        />
                        {/* Text Overlay with Black Background */}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                p: 1,
                                zIndex: 1,
                                bgcolor: 'rgba(0, 0, 0, 0.6)',
                                background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%)'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: 'common.white',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {destination.name}
                            </Typography>
                        </Box>
                    </Link>
                ))}
            </Box>
        </>
    )
}

const DesktopView: React.FC<{
    title: string
    destinations: Destination[]
    isRTL: boolean
    scrollContainerRef: React.RefObject<HTMLDivElement>
    onScroll: (direction: 'left' | 'right') => void
    t: TranslationFunction
    onDestinationClick?: (href: string) => boolean
}> = ({ title, destinations, isRTL, scrollContainerRef, onScroll, t, onDestinationClick }) => {
    const handleLeftScroll = () => onScroll(isRTL ? 'right' : 'left')
    const handleRightScroll = () => onScroll(isRTL ? 'left' : 'right')

    return (
        <>
            {/* Header Section */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    position: 'relative'
                }}
            >
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider', mr: 2 }} />
                <Typography
                    component="h2"
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '1.5rem',
                        textAlign: 'center'
                    }}
                >
                    {title}
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider', ml: 2 }} />
            </Box>

            {/* Desktop Carousel View */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    position: 'relative',
                    gap: 2,
                    direction: isRTL ? 'rtl' : 'ltr'
                }}
            >
                <IconButton
                    onClick={handleLeftScroll}
                    sx={{
                        flexShrink: 0,
                        width: 48,
                        height: 48,
                        position: 'absolute',
                        left: -60,
                        zIndex: 20
                    }}
                >
                    {isRTL ? <ChevronRight fontSize="large" /> : <ChevronLeft fontSize="large" />}
                </IconButton>

                <Box
                    ref={scrollContainerRef}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        scrollBehavior: 'smooth',
                        gap: 3,
                        direction: isRTL ? 'rtl' : 'ltr',
                        pb: 1,
                        mx: 2,
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    }}
                >
                    {destinations.map((destination, index) => (
                        <DesktopDestinationCard
                            key={destination.id}
                            destination={destination}
                            t={t}
                            onDestinationClick={onDestinationClick}
                            priority={index < 2}
                        />
                    ))}
                </Box>

                <IconButton
                    onClick={handleRightScroll}
                    sx={{
                        flexShrink: 0,
                        width: 48,
                        height: 48,
                        position: 'absolute',
                        right: -60,
                        zIndex: 20
                    }}
                >
                    {isRTL ? <ChevronLeft fontSize="large" /> : <ChevronRight fontSize="large" />}
                </IconButton>
            </Box>
        </>
    )
}

const DesktopDestinationCard: React.FC<{
    destination: Destination
    t: TranslationFunction
    onDestinationClick?: (href: string) => boolean
    priority?: boolean
}> = ({ destination, t, onDestinationClick, priority }) => {
    return (
        <Link
            href={destination.href}
            onClick={event => {
                if (onDestinationClick?.(destination.href)) {
                    event.preventDefault()
                }
            }}
            target="_blank"
            sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                height: '350px',
                flex: '0 0 calc(25% - 18px)',
                minWidth: 0,
                bgcolor: 'common.white',
                display: 'block',
                textDecoration: 'none',
                cursor: 'pointer',
                scrollSnapAlign: 'start',
                flexShrink: 0,
                '&:hover .hover-overlay': {
                    opacity: 1
                },
                '&:hover .hover-content': {
                    opacity: 1
                },
                '&:hover .normal-content': {
                    opacity: 0
                },
                '&:hover .destination-image': {
                    transform: 'scale(1.1)',
                    transition: 'transform 0.7s ease-in-out'
                }
            }}
        >
            <Box
                className="destination-image"
                sx={{ transition: 'transform 0.3s ease-in-out', height: '100%', width: '100%' }}
            >
                <LazyImage
                    src={destination.imageUrl}
                    alt={destination.name}
                    sizes="(min-width: 1200px) 270px, calc(25% - 18px)"
                    priority={priority}
                />
            </Box>

            <Box
                className="hover-overlay"
                sx={theme => ({
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.6)',
                    zIndex: 2,
                    opacity: 0,
                    transition: 'opacity 0.3s ease-in-out'
                })}
            />

            <Box
                className="normal-content"
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 2,
                    zIndex: 3,
                    opacity: 1,
                    transition: 'opacity 0.3s ease-in-out',
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%)'
                }}
            >
                <Typography
                    sx={{
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        color: 'common.white',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {destination.name}
                </Typography>
            </Box>

            <Box
                className="hover-content"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 3,
                    opacity: 0,
                    transition: 'opacity 0.3s ease-in-out',
                    p: 3,
                    gap: 2
                }}
            >
                <Typography
                    sx={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: 'common.white',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%'
                    }}
                >
                    {destination.name}
                </Typography>

                <Button
                    variant="contained"
                    sx={{
                        bgcolor: 'secondary.main',
                        color: 'common.black',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        py: 1,
                        px: 2,
                        borderRadius: 1,
                        '&:hover': {
                            bgcolor: 'secondary.dark'
                        }
                    }}
                    onClick={e => {
                        const handled = onDestinationClick?.(destination.href)
                        if (handled) {
                            e.preventDefault()
                            return
                        }
                        e.preventDefault()
                        window.open(destination.href, '_blank')
                    }}
                >
                    {t('bookNow')}
                </Button>
            </Box>
        </Link>
    )
}

export type { TrendingDestinationsProps }
